import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import styles from "./LeagueList.module.scss";
import { useUser } from "../../Context/UserContext/UserContext";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { apiGet, apiPost, apiPut } from "../../utils/apiUtils";
import { useFeedback } from "../../Context/FeedbackContext/FeedbackContext";
import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { isSystemAdmin } from "../../utils/moderatorUtils";
import { filterVisibleLeagues } from "../../utils/leagueVisibility";
import { LeagueStatusChip } from "../../Components/LeagueStatusChip/LeagueStatusChip";
import {
  compareLeaguesActiveFirst,
  isFinishedLeagueApiError,
  isLeagueFinished,
} from "../../utils/leagueStatus";

type LeagueParticipant = {
  user_id: string;
  username: string;
  role: string;
};

type LeaguePetition = {
  userId: string;
  username: string;
  status: string;
  date: string;
};

type LeaguesTypes = {
  // DB fields
  id: string;
  league_name: string;
  created_at?: string;
  created_by?: string;
  tier?: string;
  status?: string;
  is_private: boolean;
  current_participants?: number;
  updated_at?: string;
  description?: string | null;

  // Enriched fields from API.
  // GET /api/leagues no longer returns participants; detail GET /api/leagues/:id still does.
  participants?: LeagueParticipant[];
  participantsCount?: number;
  participantPetitions: LeaguePetition[];
  moderatorPetitions?: LeaguePetition[];
  moderatorArray: string[];
  quinipolosToAnswer: any[];
  leaguesToCorrect: any[];

  // Some endpoints (e.g., create) may return camelCase
  isPrivate?: boolean;
};

const participantCount = (league: LeaguesTypes) =>
  league.participantsCount ?? league.current_participants ?? 0;

const LeagueList = () => {
  const navigate = useNavigate();
  const [leagueListData, setLeagueListData] = useState<LeaguesTypes[]>([]);
  const [joinedLeagueIds, setJoinedLeagueIds] = useState<Set<string>>(
    () => new Set()
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { setFeedback } = useFeedback();
  const { t } = useTranslation();

  const { userData, updateUser } = useUser();

  // List payload no longer includes participants. Membership lives on the profile.
  const isUserInLeague = useCallback(
    (league: LeaguesTypes) => {
      if (joinedLeagueIds.has(league.id)) return true;
      const inUserLeagues = (userData.userLeagues ?? []).some(
        (membership) => membership.league_id === league.id
      );
      if (inUserLeagues) return true;
      return (userData.leagues ?? []).some(
        (membership) => membership.leagueId === league.id
      );
    },
    [joinedLeagueIds, userData.userLeagues, userData.leagues]
  );

  const markLeagueJoined = (league: LeaguesTypes) => {
    setJoinedLeagueIds((prev) => {
      if (prev.has(league.id)) return prev;
      const next = new Set(prev);
      next.add(league.id);
      return next;
    });

    const userLeagues = userData.userLeagues ?? [];
    const leagues = userData.leagues ?? [];
    const hasUserLeague = userLeagues.some(
      (membership) => membership.league_id === league.id
    );
    const hasLeague = leagues.some(
      (membership) => membership.leagueId === league.id
    );
    if (hasUserLeague && hasLeague) return;

    updateUser({
      userLeagues: hasUserLeague
        ? userLeagues
        : [...userLeagues, { league_id: league.id, role: "user" }],
      leagues: hasLeague
        ? leagues
        : [
            ...leagues,
            {
              leagueId: league.id,
              leagueName: league.league_name,
              moderatorArray: [],
              participants: [],
              leagueImage: "",
            },
          ],
    });
  };

  // Helper function to check if user has a pending petition
  const hasPendingPetition = (league: LeaguesTypes) => {
    return (league.participantPetitions ?? []).some(
      (petition) =>
        petition.username === userData.username && petition.status === "pending"
    );
  };

  const fetchLeagueListData = useCallback(async () => {
    // Fetch data logic
    apiGet<LeaguesTypes[]>(`/api/leagues`)
      .then((data) => {
        const filtered = filterVisibleLeagues(
          data as any,
          userData.role
        ) as any;
        setLeagueListData(filtered as any);
      })
      .catch((error) => {
        console.log(error);
        setFeedback({
          message: "Error cargando los datos de la liga",
          severity: "error",
          open: true,
        });
      });
    setLoading(false);
  }, [setFeedback, userData.role]);

  const handleJoinLeague = (league: LeaguesTypes) => {
    // Members and admins open the league. That is not a join.
    if (league && (isUserInLeague(league) || isSystemAdmin(userData.role))) {
      navigate("/league-dashboard?id=" + league.id);
      return;
    }

    if (isLeagueFinished(league)) {
      setFeedback({
        message: t("leagueFinishedJoinBlocked"),
        severity: "info",
        open: true,
      });
      return;
    }

    if (league?.is_private) {
      apiPost(`/api/leagues/${league.id}/request-participant`, {
        userId: userData.userId,
        username: userData.username,
      })
        .then((data: any) => {
          setLeagueListData(data);
          setFeedback({
            message: t("requestSent"),
            severity: "success",
            open: true,
          });
        })
        .catch((error) => {
          console.log(error);
          setFeedback({
            message: isFinishedLeagueApiError(error)
              ? t("leagueFinishedJoinBlocked")
              : t("errorJoiningLeague"),
            severity: isFinishedLeagueApiError(error) ? "info" : "error",
            open: true,
          });
        });
      return;
    }

    const leagueIsListed = leagueListData.some((l) => l.id === league.id);

    if (leagueIsListed) {
      apiPut(`/api/leagues/${league.id}/join`, {
        leagueId: league.id,
        username: userData.username,
      })
        .then(() => {
          setLeagueListData((prevData) =>
            prevData.map((item) => {
              if (item.id !== league.id) return item;
              return {
                ...item,
                participantsCount: participantCount(item) + 1,
              };
            })
          );
          markLeagueJoined(league);
          setFeedback({
            message: t("joinedLeague"),
            severity: "success",
            open: true,
          });
        })

        .catch((error) => {
          console.log(error);
          setFeedback({
            message: isFinishedLeagueApiError(error)
              ? t("leagueFinishedJoinBlocked")
              : t("errorJoiningLeague"),
            severity: isFinishedLeagueApiError(error) ? "info" : "error",
            open: true,
          });
        });
    }
  };

  const renderLeagueAction = (league: LeaguesTypes) => {
    const alreadyIn = isUserInLeague(league);
    const admin = isSystemAdmin(userData.role);
    const joinClosed = isLeagueFinished(league) && !alreadyIn && !admin;
    const actionTitle =
      alreadyIn || admin
        ? t("goToLeague")
        : joinClosed
        ? t("leagueFinishedJoinBlocked")
        : hasPendingPetition(league)
        ? t("pendingRequest")
        : t("joinLeague");
    const actionButton = (
      <LoadingButton
        variant="contained"
        style={{
          minWidth: "fit-content",
          width: "100%",
          justifyContent: "flex-start",
          textAlign: "left",
        }}
        className={`gradient-primary`}
        onClick={() => handleJoinLeague(league)}
        loading={!leagueListData}
        disabled={hasPendingPetition(league) || joinClosed}
        aria-label={actionTitle}
      >
        {alreadyIn || admin
          ? t("go")
          : hasPendingPetition(league)
          ? t("pending")
          : t("join")}
      </LoadingButton>
    );

    return (
      <Tooltip title={actionTitle}>
        {joinClosed || hasPendingPetition(league) ? (
          <span style={{ display: "inline-flex", width: "100%" }}>
            {actionButton}
          </span>
        ) : (
          actionButton
        )}
      </Tooltip>
    );
  };

  const renderVisibility = (league: LeaguesTypes) => {
    const isPrivate = Boolean(league.is_private);
    const label = isPrivate ? t("private") : t("public");

    return (
      <Tooltip title={label}>
        <span
          role="img"
          aria-label={label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isPrivate ? (
            <LockIcon fontSize="small" />
          ) : (
            <PublicIcon fontSize="small" />
          )}
        </span>
      </Tooltip>
    );
  };

  const displayLeagues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = leagueListData.filter((league) => {
      if (!query) return true;
      const nameMatch = league.league_name?.toLowerCase().includes(query);
      return Boolean(nameMatch);
    });

    const withMembershipPriority = filtered.slice().sort((a, b) => {
      const byStatus = compareLeaguesActiveFirst(a, b);
      if (byStatus !== 0) return byStatus;

      const aIn = isUserInLeague(a) ? 1 : 0;
      const bIn = isUserInLeague(b) ? 1 : 0;
      if (aIn !== bIn) return bIn - aIn; // in-league first

      const aCount = participantCount(a);
      const bCount = participantCount(b);
      return bCount - aCount; // desc by players
    });

    return withMembershipPriority;
  }, [leagueListData, searchQuery, isUserInLeague]);

  useEffect(() => {
    setLoading(true);
    fetchLeagueListData();
  }, [fetchLeagueListData]);

  return (
    <div className={styles.leagueListContainer}>
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          p: 2,
          pt: 4,
          pb: 4,
          borderRadius: "20px",
          mb: 2,
        }}
      >
        <h1 style={{ marginBottom: 20 }}>{t("leagues")}</h1>
        <div style={{ marginBottom: 16, display: "flex" }}>
          <TextField
            fullWidth
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("search")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>
        {loading || leagueListData.length === 0 ? (
          <CircularProgress sx={{ m: 4 }} />
        ) : (
          <TableContainer
            component={Paper}
            sx={{ boxShadow: "none", maxHeight: 520 }}
          >
            <Table aria-label="simple table" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t("name")}</TableCell>
                  <TableCell align="center">
                    <MoreHorizIcon />
                  </TableCell>
                  <TableCell align="center">
                    <EmojiPeopleIcon />
                  </TableCell>
                  <TableCell align="center">
                    <LockIcon />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayLeagues?.map((league) => (
                  <TableRow
                    key={league.league_name}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell
                      style={{ fontWeight: "bold" }}
                      component="th"
                      scope="row"
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {league.league_name}
                        <LeagueStatusChip status={league.status} />
                      </span>
                    </TableCell>
                    <TableCell align="left">{renderLeagueAction(league)}</TableCell>
                    <TableCell
                      style={{ paddingLeft: 0, paddingRight: 0 }}
                      align="center"
                    >
                      {participantCount(league)}
                    </TableCell>
                    <TableCell align="center">{renderVisibility(league)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </div>
  );

  // Add more state variables as needed
};

export default LeagueList;
